import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import isEmpty from 'lodash-es/isEmpty';

import { goodOptionValidation, resolvePriceOpen } from '@ecp/common/src/utils/good/goodUtil';
import useOptionCountValidator from '@ecp/common/src/hooks/good/option/useGoodOptionValidator';
import theme from '@ecp/common/src/style/theme/default';
import { FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import BottomSheets from '@ecp/common/src/components/bottomSheets/mo/BottomSheets';
import { RectanglePageButton } from '@ecp/common/src/components/button/mo/Button';
import { DIALOG_MESSAGE, PD_GOOD_CMPS_DIVI, PD_GOOD_TYPE } from '@ecp/common/src/const/good';
// ECMJ 프로그램 아이디 : REQ_대한항공 적립몰_01_01 (jg26.kim)
import { SITE_SKYPASS, SITE_KOREANAIR_SAVINGS } from '@ecp/common/src/const/site/siteNumberConst.js';

import { goodOptionListState } from '@mo-recoil/good/atom';
import { withCartBadgeCount } from '@mo-recoil/order/selector';
import { withAdultCertification } from '@mo-recoil/common/user/selector';
import useSiteInfo from '@mo-hooks/common/useSiteInfo';
import { useAlertDialog, useConfirmDialog } from '@mo-hooks/common/useModal';
import goodApi from '@mo-apis/good/goodApi';
import orderApi from '@mo-apis/order/orderApi';
import recopickApi from '@mo-apis/interface/recopickApi';
import InformationLimitPop from '@mo-components/good/InformationLimitPop';
import GoodCtaButtonOption from '@mo-pages/good/components/cta/option/GoodCtaButtonOption';

//정기배송상품은 장바구니 불가라 관련 데이터는 NULL 혹은 빈값으로 처리
const CartPopup = ({ goodNumber, open, setCartPopupOpen, handleCartPopupSuccess }) => {
  const [goodDetail, setGoodDetail] = useState({});
  const [adultPopupOpen, setAdultPopupOpen] = useState(false);
  const { showAlertDialog } = useAlertDialog();
  const { showConfirmDialog } = useConfirmDialog();
  const navigate = useNavigate();
  const [confirmButtonText, setConfirmButtonText] = useState('장바구니 담기');
  const [confirmButtonType, setConfirmButtonType] = useState('Primary');
  const { priceFormat, isPriceOpen: siteIsPriceOpen } = useSiteInfo();

  const [goodOptionList, setGoodOptionList] = useRecoilState(goodOptionListState);
  const setCartBadgeCount = useSetRecoilState(withCartBadgeCount);
  const isAdultVerified = useRecoilValue(withAdultCertification);
  const { siteNumber } = useSiteInfo();
  // ECMJ 프로그램 아이디 : REQ_대한항공 적립몰_01_01 (jg26.kim)
  const isSkypass = useMemo(() => siteNumber === SITE_SKYPASS || siteNumber === SITE_KOREANAIR_SAVINGS, [siteNumber]);
  // REQ_DS_05_01
  const isPriceOpen = useMemo(() => resolvePriceOpen(goodDetail?.priceExpsUseYn, siteIsPriceOpen), [goodDetail?.priceExpsUseYn, siteIsPriceOpen]);

  const isGroup = useMemo(() => {
    return goodDetail?.goodDivisionCode === PD_GOOD_CMPS_DIVI.GROUP;
  }, [goodDetail?.goodDivisionCode]);

  const isRental = useMemo(() => {
    return PD_GOOD_TYPE.RENTAL === goodDetail?.goodTypeCode;
  }, [goodDetail?.goodTypeCode]);

  const isRegularDelivery = useMemo(() => {
    return PD_GOOD_TYPE.REGULAR_DELIVERY === goodDetail?.goodTypeCode;
  }, [goodDetail?.goodTypeCode]);

  const { optionCtaButtonValidator } = useOptionCountValidator(showAlertDialog);

  const handleCloseCartPopup = useCallback(() => {
    setGoodDetail({});
    setCartPopupOpen(false);
    setAdultPopupOpen(false);
    setGoodOptionList([]);
  }, [setGoodDetail, setCartPopupOpen, setAdultPopupOpen]);

  const fetchGoodDetail = useCallback(async () => {
    setGoodDetail({});
    setGoodOptionList([]);
    const { result = {} } = await goodApi.getGoodDetail(goodNumber);
    if (!isEmpty(result)) {
      setGoodDetail(result);
      setAdultPopupOpen(result.adultGood);
      if (!result?.multiItem && !(result?.goodDivisionCode === PD_GOOD_CMPS_DIVI.GROUP)) {
        setGoodOptionList((prevOptions) => [...prevOptions, { ...result?.itemList?.[0], count: 1 }]);
      }
      setConfirmButtonType(result.stopSale || result.soldOut || !result.showBtnCart ? 'Secondary' : 'Third');
      setConfirmButtonText(
        result.stopSale
          ? '판매중지'
          : result.soldOut
          ? '품절'
          : !result.showBtnCart
          ? '장바구니 불가 상품입니다'
          : '장바구니 담기'
      );
    } else {
      showAlertDialog('상품이 존재하지 않습니다.', handleCloseCartPopup);
    }
  }, [goodNumber, setGoodOptionList, setGoodDetail, setAdultPopupOpen, showAlertDialog, open]);

  const handleLimitConfirm = useCallback(async () => {
    if (open) {
      await fetchGoodDetail();
      setAdultPopupOpen(false);
    }
  }, [goodNumber, open]);

  const orderValidator = useCallback(() => {
    return optionCtaButtonValidator(goodOptionList, isRegularDelivery, goodDetail?.regularDelivery, null);
  }, [goodOptionList, isRegularDelivery, optionCtaButtonValidator, goodDetail?.regularDelivery]);

  const handleCartButtonClick = useCallback(async () => {
    if (!orderValidator()) {
      return;
    }

    if (goodDetail?.showBtnBulkp) {
      const currentOrderQuantity = goodOptionList.reduce((acc, elem) => {
        return acc + (elem.additionalItem ? 0 : elem.count);
      }, 0);
      if (goodDetail?.bulkpOrdPossibleMinQty <= currentOrderQuantity) {
        showAlertDialog(DIALOG_MESSAGE.OPTION_QTY_IS_ONLY_BULKP_ORDER);
        return;
      }
    }

    const checkOrderAvailableRequest = goodOptionList.map((item) => ({
      ...item,
      orderQuantity: item?.count,
      orderAmount: item?.discountPrice * item?.count,
    }));

    const { result: availableResult = [] } = await goodApi.checkOrderAvailableWithCart(checkOrderAvailableRequest);
    const { message = '' } = goodOptionValidation({
      checkOrderAvailableResult: availableResult,
      selectedOptionList: checkOrderAvailableRequest,
      isBulkpOrder: false,
      priceFormat,
      ...goodDetail,
    });
    if (message) {
      showAlertDialog(message);
      return;
    }

    const itemList = goodOptionList?.map((option) => ({
      goodNumber: option?.goodNumber,
      itemNumber: option?.itemNumber,
      virtualVendorNumber: option?.virtualVendorNumber,
      orderQuantity: option?.count,
    }));

    const { code, result } = await orderApi.createCart(itemList, '10', null, false, 'POPUP');
    if (code === '200') {
      setCartBadgeCount(result?.cartCount);
      showConfirmDialog(
        DIALOG_MESSAGE.CART_SUCCESS,
        () => {
          navigate('/cart');
          handleCloseCartPopup();
          handleCartPopupSuccess?.();
        },
        () => {
          handleCloseCartPopup();
          handleCartPopupSuccess?.();
        },
        DIALOG_MESSAGE.GO_TO_CART,
        DIALOG_MESSAGE.CONTINUE_SHOPPING
      );
      if (!isSkypass) {
        await recopickApi.createRecommendationBasketLog('basket', { itemList }, history?.prev, history?.current);
      }
    } else {
      let message;
      if (code === 'OD_14102') {
        message = DIALOG_MESSAGE.CART_MAX_COUNT;
      } else if (code === 'OD_24348') {
        message = DIALOG_MESSAGE.ITEM_SOLD_OUT;
      } else if (code === 'OD_14104') {
        message = DIALOG_MESSAGE.CART_PRICE_EXPS_NOT_AVAILABLE;
      } else {
        message = DIALOG_MESSAGE.CART_FAIL;
      }
      showAlertDialog(message, handleCloseCartPopup);
    }
  }, [
    goodDetail,
    goodOptionList,
    history,
    navigate,
    orderValidator,
    setCartBadgeCount,
    showAlertDialog,
    showConfirmDialog,
    handleCloseCartPopup,
    handleCartPopupSuccess,
  ]);

  useEffect(() => {
    (async () => {
      if (goodNumber && open) {
        await fetchGoodDetail();
      }
    })();
  }, [goodNumber, open]);

  return adultPopupOpen && !isAdultVerified ? (
    <InformationLimitPop
      key={`${goodNumber}_limit`}
      open={adultPopupOpen}
      onClose={handleCloseCartPopup}
      onConfirm={handleLimitConfirm}
    />
  ) : (
    !isEmpty(goodDetail) && (
      <BottomSheets
        key={goodNumber}
        open={open}
        onClose={handleCloseCartPopup}
        headerType={'accordion'}
        closable={false}
        arrowCenter
        ariaLabel={'닫기'}
        cartPopup={true}
      >
        <FlexBoxColumn width={'360px'}>
          {!goodDetail.stopSale ? (
            <>
              <GoodCtaButtonOption
                multiItem={goodDetail.multiItem}
                itemNodeList={goodDetail.itemNodeList}
                additionItemList={goodDetail.additionItemList}
                stockManagement={goodDetail.stockManagement}
                stockQtyDisplay={goodDetail.stockQtyDisplay}
                isGroup={isGroup}
                isRental={isRental}
                isRegularDelivery={isRegularDelivery}
                soldOut={goodDetail.soldOut}
                subGoodList={goodDetail.subGoodList}
                regularDelivery={goodDetail.regularDelivery}
                displayEndDate={goodDetail.displayEndDate}
                showBtnBulkp={goodDetail.showBtnBulkp}
                bulkpOrdPossibleMinQty={goodDetail.bulkpOrdPossibleMinQty}
                ordPossibleMinQty={goodDetail.ordPossibleMinQty}
                ordPossibleMinAmount={goodDetail.ordPossibleMinAmount}
                ordPossibleMinFormattedAmount={goodDetail?.ordPossibleMinFormattedAmount}
                ordPossibleMaxQty={goodDetail.ordPossibleMaxQty}
                ordPossibleMaxAmount={goodDetail.ordPossibleMaxAmount}
                ordPossibleMaxFormattedAmount={goodDetail?.ordPossibleMaxFormattedAmount}
                ordPossibleMaxQtyStandardCode={goodDetail.ordPossibleMaxQtyStandardCode}
                isPriceOpen={isPriceOpen}
              />
              {confirmButtonType === 'Third' ? (
                <StyledPageButton type={confirmButtonType} onClick={handleCartButtonClick} data-ds-label2={'basket'}>
                  {confirmButtonText}
                </StyledPageButton>
              ) : (
                <StyledPageButton type={confirmButtonType} onClick={handleCloseCartPopup}>
                  {confirmButtonText}
                </StyledPageButton>
              )}
            </>
          ) : (
            <StyledPageButton type={confirmButtonType} onClick={handleCloseCartPopup}>
              {confirmButtonText}
            </StyledPageButton>
          )}
        </FlexBoxColumn>
      </BottomSheets>
    )
  );
};

export default CartPopup;

CartPopup.propTypes = {
  goodNumber: PropTypes.string,
  open: PropTypes.bool,
  setCartPopupOpen: PropTypes.func,
  handleCartPopupSuccess: PropTypes.func,
};

const StyledPageButton = styled(RectanglePageButton)`
  &::after {
    position: absolute;
    bottom: -1px;
    content: '';
    height: 2px;
    left: 0;
    right: 0;
    background-color: ${({ type = 'Primary' }) => theme.color[type.toLowerCase()]};
  }
`;


